import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../services/api';

interface SuccessState {
  token?: string;
  title?: string;
  /** Vrai pour le dernier signataire : le document signé est téléchargeable dès qu'il est scellé */
  allSigned?: boolean;
}

const POLL_EVERY_MS = 3000;
const GIVE_UP_AFTER_MS = 3 * 60 * 1000;

function SuccessPage() {
  const { state } = useLocation();
  const { token, title, allSigned } = (state ?? {}) as SuccessState;
  const wantsDownload = Boolean(allSigned && token);

  // 'waiting' : scellement en cours ; 'ready' : prêt à télécharger ; 'late' : trop long, le mail suffira
  const [phase, setPhase] = useState<'waiting' | 'ready' | 'late'>('waiting');
  const blobUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!wantsDownload) return;
    let cancelled = false;
    const startedAt = Date.now();

    const attempt = async () => {
      try {
        const response = await api.post('/session/document/signed', { token }, { responseType: 'blob' });
        if (cancelled) return;
        blobUrl.current = URL.createObjectURL(response.data);
        setPhase('ready');
      } catch {
        if (cancelled) return;
        if (Date.now() - startedAt > GIVE_UP_AFTER_MS) {
          setPhase('late');
        } else {
          setTimeout(attempt, POLL_EVERY_MS);
        }
      }
    };
    attempt();

    return () => {
      cancelled = true;
      if (blobUrl.current) URL.revokeObjectURL(blobUrl.current);
    };
  }, [wantsDownload, token]);

  const filename = `${title ?? 'document'}_signe.pdf`;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="card">
          <img src="/img/success.webp" alt="" className="mx-auto mb-2 h-48 w-48 rounded-3xl object-contain" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Bravo, c'est signé !
          </h1>
          <p className="text-gray-600 mb-6">
            {wantsDownload
              ? 'Vous étiez le dernier à signer : le document est maintenant signé par toutes les parties.'
              : 'Votre signature électronique a été apposée sur le document.'}
          </p>

          {wantsDownload && phase === 'waiting' && (
            <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4" role="status">
              <p className="text-sm text-blue-800">
                Finalisation du document en cours (scellement et horodatage)… le bouton de téléchargement apparaît dans un instant.
              </p>
            </div>
          )}

          {wantsDownload && phase === 'ready' && blobUrl.current && (
            <a
              href={blobUrl.current}
              download={filename}
              className="mb-6 block w-full rounded-lg bg-blue-600 px-6 py-3 font-medium hover:bg-blue-700"
              style={{ color: '#ffffff' }}
            >
              Télécharger le document signé
            </a>
          )}

          {wantsDownload && phase === 'late' && (
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-800">
                La finalisation prend plus de temps que prévu. Vous recevrez le document signé par e-mail dès qu'elle sera terminée.
              </p>
            </div>
          )}

          <p className="text-sm text-gray-500 mb-6">
            Vous recevrez aussi par e-mail {wantsDownload ? 'le document signé et ' : 'une copie signée et '}le rapport de preuve.
          </p>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              Cette signature ne peut être écartée comme preuve au seul motif qu'elle est électronique (règlement eIDAS art. 25).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SuccessPage;
